// API配置文件 - Mock数据版本
// 为演示需求提供完整的数据支持

// 导入所有Mock数据
import { 
  mockDelay, 
  createMockResponse, 
  coreBusinessData,
  productData, 
  newsData, 
  exampleData, 
  teamData, 
  qualificationData, 
  technicalData, 
  satelliteOrbitData, 
  slideshowData, 
  serviceData, 
  statisticsData, 
  contactData 
} from '@/data/mockData';

// 原始axios配置保留，便于将来切换到真实后端
// import axios from "axios";
// let base = 'http://localhost:8087';

// API路由映射
const mockRoutes = {
  // 首页数据
  '/slideshow': () => slideshowData.slides,
  '/findAllSlideshow': () => slideshowData.slides,
  
  // 核心业务数据（新增）
  '/coreBusinessLines': () => coreBusinessData.businessLines,
  '/coreTechnologies': () => coreBusinessData.coreTechnologies,
  '/manufacturingCapability': () => coreBusinessData.manufacturingCapability,
  
  // 产品相关
  '/products': () => productData.products,
  '/productCategories': () => productData.categories,
  '/findProductByProductType': (params) => {
    const { productType } = params;
    return productData.products.filter(p => p.typeId === parseInt(productType));
  },
  '/findProductByProductId': (params) => {
    const { productId } = params;
    return productData.products.find(p => p.id === parseInt(productId));
  },
  
  // 新闻相关
  '/news': () => newsData.articles,
  '/findNewsByNewsId': (params) => {
    const { newsId } = params;
    return newsData.articles.find(n => n.id === parseInt(newsId));
  },
  '/findArticleByArticleId': (params) => {
    const { articleId } = params;
    return newsData.articles.find(n => n.id === parseInt(articleId));
  },
  '/findRecentArticle': (params) => {
    const { currentArticleId } = params;
    // 返回除当前文章外的其他文章作为相关文章
    return newsData.articles.filter(n => n.id !== parseInt(currentArticleId)).slice(0, 3);
  },
  
  // 案例相关  
  '/examples': () => exampleData.cases,
  '/findExampleByExampleType': (params) => {
    const { exampleType } = params;
    return exampleData.cases.filter(e => e.typeId === parseInt(exampleType));
  },
  '/findExampleByExampleId': (params) => {
    const { exampleId } = params;
    return exampleData.cases.find(e => e.id === parseInt(exampleId));
  },
  
  // 团队数据
  '/team': () => teamData.members,
  '/qualifications': () => qualificationData.certifications,
  '/technical': () => technicalData.capabilities,
  
  // 卫星轨道数据
  '/satelliteOrbit': () => satelliteOrbitData.orbits,
  
  // 服务数据
  '/services': () => serviceData.services,
  
  // 统计数据
  '/statistics': () => statisticsData.metrics,
  
  // 联系信息
  '/contact': () => contactData.info
};

// Mock API调用函数
export const getRequest = async (url, params = {}) => {
  await mockDelay();
  
  console.log(`[Mock API] ${url}`, params);
  
  try {
    // 解析URL路径和参数
    let path = url.split('?')[0];
    
    // 动态参数解析
    const urlParams = new URLSearchParams(url.split('?')[1] || '');
    const combinedParams = { ...params };
    
    // 从URL参数中提取动态参数
    for (const [key, value] of urlParams) {
      combinedParams[key] = value;
    }
    
    // 处理路径参数（如 /products/1 -> productId: 1）
    const pathSegments = path.split('/').filter(Boolean);
    if (pathSegments.length > 1) {
      const lastSegment = pathSegments[pathSegments.length - 1];
      if (/^\d+$/.test(lastSegment)) {
        // 根据路径确定参数名
        if (path.includes('/products/')) {
          combinedParams.productId = lastSegment;
          path = '/findProductByProductId';
        } else if (path.includes('/news/')) {
          combinedParams.newsId = lastSegment;
          path = '/findNewsByNewsId';
        } else if (path.includes('/examples/')) {
          combinedParams.exampleId = lastSegment;
          path = '/findExampleByExampleId';
        } else if (path.includes('/findArticleByArticleId/')) {
          combinedParams.articleId = lastSegment;
          path = '/findArticleByArticleId';
        } else if (path.includes('/findRecentArticle/')) {
          combinedParams.currentArticleId = lastSegment;
          path = '/findRecentArticle';
        }
      }
    }
    
    // 查找对应的mock处理器
    const handler = mockRoutes[path];
    
    if (handler) {
      const data = handler(combinedParams);
      return createMockResponse(data);
    } else {
      console.warn(`[Mock API] No handler found for: ${path}`);
      return createMockResponse(null, false, `API路径 ${path} 未找到`);
    }
    
  } catch (error) {
    console.error('[Mock API] Error:', error);
    return createMockResponse(null, false, error.message);
  }
};

// 原始axios请求函数（注释，保留以备将来使用）
/*
export const getRequest = (url, params) => {
  return axios({
    method: 'get',
    url: `${base}${url}`,
    data: params,
  });
};
*/

// 导出mock数据供直接使用
export { getRequest as default };

// API辅助函数
export const api = {
  // 首页数据
  getHomepageData: () => getRequest('/slideshow'),
  
  // 核心业务数据（新增）
  getCoreBusinessLines: () => getRequest('/coreBusinessLines'),
  getCoreTechnologies: () => getRequest('/coreTechnologies'), 
  getManufacturingCapability: () => getRequest('/manufacturingCapability'),
  
  // 产品数据
  getProducts: () => getRequest('/products'),
  getProductCategories: () => getRequest('/productCategories'),
  getProductById: (id) => getRequest(`/products/${id}`),
  getProductsByType: (typeId) => getRequest('/findProductByProductType', { productType: typeId }),
  
  // 新闻数据
  getNews: () => getRequest('/news'),
  getNewsById: (id) => getRequest(`/news/${id}`),
  
  // 案例数据
  getExamples: () => getRequest('/examples'),
  getExampleById: (id) => getRequest(`/examples/${id}`),
  getExamplesByType: (typeId) => getRequest('/findExampleByExampleType', { exampleType: typeId }),
  
  // 其他数据
  getTeam: () => getRequest('/team'),
  getQualifications: () => getRequest('/qualifications'),
  getTechnical: () => getRequest('/technical'),
  getSatelliteOrbit: () => getRequest('/satelliteOrbit'),
  getServices: () => getRequest('/services'),
  getStatistics: () => getRequest('/statistics'),
  getContact: () => getRequest('/contact')
};

console.log('[Mock API] 洞一科技官网Mock API系统已初始化');
console.log('[Mock API] 可用路由:', Object.keys(mockRoutes));
